// Main matching engine — tries P1 (reference) → P2 (subset-sum) → unmatched.

import type { MatchCandidate, MatchLineItem, MatchResult, NearMissCandidate, UnmatchedReason, UsedCandidates, LineItemMatchReason } from './types.ts';
import type { InvoiceLink } from '@shared/types/invoice.ts';
import { matchByReference } from './p1Match.ts';
import { matchBySubsetSum } from './p2Match.ts';
import { isZero } from '../../../calc.ts';

/**
 * Match invoice line items to candidates (transactions or transports).
 * Tries P1 (reference) → P2 (subset-sum) → unmatched.
 */
export function matchLineItems(
  invoiceId: number,
  lineItems: MatchLineItem[],
  currency: string,
  candidates: MatchCandidate[],
  sellDate?: string | null,
  /** Shared across invoices in a batch to prevent double-claiming candidates. */
  sharedUsed?: UsedCandidates,
  /** Paid invoices accept a candidate one calendar month off the sell date. */
  allowAdjacentMonth = false,
  /** Full vendor pool (free + taken + unpriced) for ref-miss diagnosis (finding 6). */
  refLookup?: MatchCandidate[],
): MatchResult {
  const tag = `[match:inv${invoiceId}]`;
  const nonZeroItems = lineItems.filter(li => !isZero(li.net_amount ?? 0));
  console.log(`${tag} start: ${nonZeroItems.length} non-zero line items, ${candidates.length} candidates, currency=${currency}, sellDate=${sellDate}`);

  const used: UsedCandidates = sharedUsed ?? new Map<number, Set<string>>();
  const allLinks: InvoiceLink[] = [];
  const allReasons: LineItemMatchReason[] = [];
  const approvedItemIds: number[] = [];

  for (const li of nonZeroItems) {
    const result = matchSingleLineItem(invoiceId, li, candidates, currency, sellDate, used, allowAdjacentMonth, refLookup);
    allLinks.push(...result.links);
    allReasons.push(result.reason);
    if (result.reason.match === 'approved') approvedItemIds.push(li.id);
  }

  const requiresCorrection = allReasons.some(r => r.match === 'correction_required');

  let status: MatchResult['status'];
  if (approvedItemIds.length === nonZeroItems.length && nonZeroItems.length > 0) status = 'approved';
  else status = 'pending';

  console.log(`${tag} done: status=${status}, links=${allLinks.length}, approved=${approvedItemIds.length}/${nonZeroItems.length}, requiresCorrection=${requiresCorrection}`);
  return { links: allLinks, status, line_item_reasons: allReasons, requiresCorrection };
}

type LineResolution = { links: InvoiceLink[]; reason: LineItemMatchReason };

/**
 * P1 (reference) step. Returns a TERMINAL resolution when the line carries a usable
 * reference (matched, correction-required, or ref-named-but-unmatched — in all of which
 * P2 must NOT run), or `null` when the line has no usable reference and should fall
 * through to P2. Splitting this out lets the batch matcher run P1 across ALL invoices
 * before any P2, so a reference always claims its exact transaction before a blind
 * subset-sum on another invoice can consume it.
 */
function resolveByReference(
  invoiceId: number,
  li: MatchLineItem,
  candidates: MatchCandidate[],
  currency: string,
  sellDate: string | null | undefined,
  used: UsedCandidates,
  allowAdjacentMonth: boolean,
  /** Full vendor pool for ref-miss diagnosis (finding 6). */
  refLookup?: MatchCandidate[],
): LineResolution | null {
  const tag = `[match:inv${invoiceId}:li${li.id}]`;
  // Match each line in ITS OWN currency, not the invoice header's. A line is priced in its
  // own currency; the header can be stale/divergent (e.g. a Fakturownia EUR invoice whose
  // header was synced as PLN before the currency was corrected), and gating on the header
  // would then wrongly reject a correct same-currency candidate.
  const cur = li.currency || currency;
  console.log(`${tag} trying P1 (net=${li.net_amount}, cur=${cur}, name="${li.name?.substring(0, 40)}")`);

  const p1 = matchByReference(invoiceId, li, candidates, cur, sellDate, used, allowAdjacentMonth, refLookup);
  if (p1 && p1.links.length > 0) {
    console.log(`${tag} P1 hit: ${p1.links.length} links, verified=${p1.verified}, correctionRequired=${p1.correctionRequired ?? false}`);
    return {
      links: p1.links,
      reason: {
        line_item_id: li.id, line_item_name: li.name,
        match: p1.correctionRequired ? 'correction_required' : 'approved',
        priority: 'P1',
        verify_result: p1.verifyResult,
      },
    };
  }
  // P1 found ref but verify failed — surface field mismatches in reason, no links
  if (p1 && p1.links.length === 0 && p1.verifyResult) {
    console.log(`${tag} P1 ref found but verify failed — mismatches: ${p1.verifyResult.mismatches.map(m => m.field).join(', ')}`);
    return {
      links: [],
      reason: {
        line_item_id: li.id, line_item_name: li.name,
        match: 'unmatched',
        priority: 'P1',
        verify_result: p1.verifyResult,
        unmatched_reason: 'p1_verify_failed',
        near_miss_candidates: p1.nearMisses,
      },
    };
  }

  // P1 diagnosed a MORE SPECIFIC reason (finding 6): candidate_unweighed (our unweighed
  // tx), ref_found_tx_unpriced / ref_linked_elsewhere (ref names an excluded tx),
  // currency_no_candidates (ref matched only a wrong-currency side). Terminal — block P2.
  if (p1?.refReason) {
    console.log(`${tag} unmatched (P1 refReason=${p1.refReason})`);
    return {
      links: [],
      reason: {
        line_item_id: li.id, line_item_name: li.name,
        match: 'unmatched',
        unmatched_reason: p1.refReason,
        near_miss_candidates: p1.nearMisses,
      },
    };
  }

  // P1 named a ref but no candidate carried it → P1 owns the line; do NOT fall to P2
  // (a coincidental amount match must not override an explicit, unmatched reference).
  if (p1?.refsBlockFallthrough) {
    const blockedReason: UnmatchedReason = candidates.length === 0 ? 'no_candidates' : 'p1_ref_unmatched';
    console.log(`${tag} unmatched (P1 blocked, reason=${blockedReason})`);
    return {
      links: [],
      reason: {
        line_item_id: li.id, line_item_name: li.name,
        match: 'unmatched',
        unmatched_reason: blockedReason,
        near_miss_candidates: p1?.nearMisses,
      },
    };
  }

  return null; // no usable reference — eligible for P2
}

/** P2 (subset-sum) step. Always terminal — runs only for lines with no usable reference. */
function resolveBySubsetSum(
  invoiceId: number,
  li: MatchLineItem,
  candidates: MatchCandidate[],
  currency: string,
  sellDate: string | null | undefined,
  used: UsedCandidates,
  allowAdjacentMonth: boolean,
): LineResolution {
  const tag = `[match:inv${invoiceId}:li${li.id}]`;
  // Match in the line's own currency (see resolveByReference).
  const cur = li.currency || currency;
  console.log(`${tag} trying P2 (subset-sum, cur=${cur})`);
  const p2 = matchBySubsetSum(invoiceId, li, candidates, cur, sellDate, used, allowAdjacentMonth);
  if (p2 && p2.links.length > 0) {
    console.log(`${tag} P2 hit: ${p2.links.length} links`);
    return {
      links: p2.links,
      reason: { line_item_id: li.id, line_item_name: li.name, match: 'approved', priority: 'P2' },
    };
  }
  console.log(`${tag} P2 no match`);

  // NOTE: P3 (confidence scoring) is intentionally NOT run in the automated matcher. It
  // is a REQUEST-TIME ranking aid for the manual reconcile picker (scoreCandidatesForLine
  // in p3Score.ts). The sweep never writes speculative suggestion links — P1 (firm) +
  // P2 (exact) are the only automated linkers.
  // p2 === null → truly no candidates in the (currency/vat/date-filtered) pool. Otherwise
  // carry P2's specific reason (p2_no_subset / p2_verify_failed / p2_search_exhausted;
  // finding 12 ambiguity surfaces as p2_ambiguous with near-misses).
  const unmatchedReason: UnmatchedReason = p2 === null ? 'no_candidates' : (p2.reason ?? 'p2_no_subset');
  const nearMisses: NearMissCandidate[] | undefined = p2?.nearMisses;
  console.log(`${tag} unmatched reason=${unmatchedReason}${p2?.ambiguous ? ' (ambiguous — downgraded to suggestion)' : ''}`);
  return {
    links: [],
    reason: {
      line_item_id: li.id, line_item_name: li.name,
      match: 'unmatched',
      unmatched_reason: unmatchedReason,
      near_miss_candidates: nearMisses,
    },
  };
}

function matchSingleLineItem(
  invoiceId: number,
  li: MatchLineItem,
  candidates: MatchCandidate[],
  currency: string,
  sellDate: string | null | undefined,
  used: UsedCandidates,
  allowAdjacentMonth: boolean,
  refLookup?: MatchCandidate[],
): LineResolution {
  return resolveByReference(invoiceId, li, candidates, currency, sellDate, used, allowAdjacentMonth, refLookup)
      ?? resolveBySubsetSum(invoiceId, li, candidates, currency, sellDate, used, allowAdjacentMonth);
}

// ── Global two-phase batch matching ───────────────────────────────────────

/** One invoice's matching inputs for {@link matchInvoiceBatch}. */
export interface BatchMatchInput {
  invoiceId: number;
  lineItems: MatchLineItem[];
  currency: string;
  candidates: MatchCandidate[];
  sellDate?: string | null;
  /** Paid invoices accept a candidate one calendar month off the sell date. */
  allowAdjacentMonth?: boolean;
  /** Full vendor pool (free + taken + unpriced) for ref-miss diagnosis (finding 6). */
  refLookup?: MatchCandidate[];
}

/**
 * Match many invoices in one pass with GLOBAL phase ordering:
 *   Phase A — P1 (reference) for every line of every invoice, claiming exact transactions.
 *   Phase B — P2 (subset-sum) for the remaining (reference-less) lines only.
 * A single shared `used` map spans the whole batch, so a transaction referenced by one
 * invoice can't be consumed by a blind subset-sum on another (the cross-invoice
 * starvation that per-invoice/per-chunk processing caused). Returns one MatchResult per
 * invoice, keyed by invoiceId. Pure — no DB access.
 */
export function matchInvoiceBatch(
  inputs: BatchMatchInput[],
  sharedUsed?: UsedCandidates,
): Map<number, MatchResult> {
  const used: UsedCandidates = sharedUsed ?? new Map<number, Set<string>>();
  console.log(`[match:batch] ${inputs.length} invoices — phase A (P1) then phase B (P2)`);

  // Per invoice: the non-zero line items + a slot array aligned to them.
  const perInput = inputs.map(inp => ({
    inp,
    items: inp.lineItems.filter(li => !isZero(li.net_amount ?? 0)),
    slots: [] as (LineResolution | null)[],
  }));
  for (const p of perInput) p.slots = new Array<LineResolution | null>(p.items.length).fill(null);

  // Phase A — P1 across ALL invoices first. Reference hits claim their candidates in
  // `used` before any P2 runs anywhere.
  for (const { inp, items, slots } of perInput) {
    for (let i = 0; i < items.length; i++) {
      slots[i] = resolveByReference(inp.invoiceId, items[i], inp.candidates, inp.currency, inp.sellDate, used, inp.allowAdjacentMonth ?? false, inp.refLookup);
    }
  }

  // Phase B — P2 for the lines Phase A left unresolved (no usable reference).
  for (const { inp, items, slots } of perInput) {
    for (let i = 0; i < items.length; i++) {
      if (slots[i]) continue;
      slots[i] = resolveBySubsetSum(inp.invoiceId, items[i], inp.candidates, inp.currency, inp.sellDate, used, inp.allowAdjacentMonth ?? false);
    }
  }

  // Assemble one MatchResult per invoice, line order preserved.
  const out = new Map<number, MatchResult>();
  for (const { inp, items, slots } of perInput) {
    const resolutions = slots as LineResolution[];
    const links = resolutions.flatMap(r => r.links);
    const reasons = resolutions.map(r => r.reason);
    const approved = reasons.filter(r => r.match === 'approved').length;
    const requiresCorrection = reasons.some(r => r.match === 'correction_required');
    const status: MatchResult['status'] = approved === items.length && items.length > 0 ? 'approved' : 'pending';
    console.log(`[match:batch] inv${inp.invoiceId}: status=${status}, links=${links.length}, approved=${approved}/${items.length}, requiresCorrection=${requiresCorrection}`);
    out.set(inp.invoiceId, { links, status, line_item_reasons: reasons, requiresCorrection });
  }
  return out;
}
